import React from 'react';
import { buildSongMeta } from '../songDisplay';
import './SongPanel.css';

export const SONG_PANEL_MODES = ['lyrics', 'tracks', 'videos'];

/**
 * The Song panel (Lesson Dashboard pass 3 D1/D2, 2026-09-28; pass 4 S2,
 * 2026-10-02): ONE component for everything about the song on screen, at
 * every width (RightColumn hosts it at 1200+, SongPanelSheet below).
 *
 * Pass 4: the LYRICS are the panel. Tracks and Video are no longer equal
 * tabs (Anthony: "the lyrics/tracks/videos section occupies too much
 * space"); they are two rows under the title that state their live fact
 * ("2 on this song · Track playing", "Lyric video saved · …") and open their
 * mode IN PLACE, where a **Lyrics** button comes back. A row may carry one
 * action of its own (Make a track, Find one, Stop), which never opens the
 * mode by accident: it is its own button beside the row's.
 *
 * The MODE belongs to the page (one per lesson, default Lyrics), so the
 * column and the sheet agree and the strip's "All tracks" can switch it.
 * The panes and the row facts are handed in: this component owns the chrome.
 *
 * `facts` = { tracks: { text, busy, alert, action: { label, icon, onClick } },
 *             videos: { … } }
 */
const ROWS = [
	['tracks', 'Tracks', 'fa-layer-group'],
	['videos', 'Video', 'fa-video'],
];

const FactRow = ({ kind, label, icon, fact = {}, onOpen }) => (
	<div className={`ld-songpanel__mat${fact.alert ? ' is-alert' : ''}`} data-guide={`songpanel-${kind}`}>
		<button type="button" className="ld-songpanel__matopen" onClick={onOpen} aria-label={fact.text ? `${label}: ${fact.text}. Open ${label}` : `Open ${label}`}>
			<i className={`fa-sharp fa-solid ${icon} ld-songpanel__maticon`} aria-hidden="true" />
			<b className="ld-songpanel__matlabel">{label}</b>
			<span className="ld-songpanel__matfact">
				{fact.busy && <i className="fa-sharp fa-solid fa-spinner-third fa-spin" aria-hidden="true" />}
				{fact.alert && <i className="fa-sharp fa-solid fa-triangle-exclamation ld-songpanel__matwarn" aria-hidden="true" />}
				{fact.text}
			</span>
		</button>
		{fact.action && (
			<button type="button" className={`ld-songpanel__mataction${fact.action.danger ? ' is-danger' : ''}`} data-guide={`songpanel-${kind}-action`} onClick={fact.action.onClick}>
				{fact.action.icon && <i className={`fa-sharp fa-solid ${fact.action.icon}`} aria-hidden="true" />}
				{fact.action.label}
			</button>
		)}
		<i className="fa-sharp fa-solid fa-chevron-right ld-songpanel__matchev" aria-hidden="true" />
		{typeof fact.progress === 'number' && (
			<span className="ld-songpanel__matbar" aria-hidden="true">
				<span style={{ width: `${Math.round(fact.progress * 100)}%` }} />
			</span>
		)}
	</div>
);

const SongPanel = ({ song, mode = 'lyrics', onMode, facts = {}, panes = {}, variant = 'column', headerExtra = null }) => {
	const title = song?.metadata?.title || song?.name || 'No song selected';
	const meta = buildSongMeta(song);
	const active = SONG_PANEL_MODES.includes(mode) ? mode : 'lyrics';
	const go = (m) => onMode && onMode(m);
	const modeLabel = active === 'tracks' ? 'Tracks' : active === 'videos' ? 'Video' : null;

	return (
		<section className={`ld-songpanel ld-songpanel--${variant}`} aria-label={`${title}: song panel`}>
			<header className="ld-songpanel__head">
				<div className="ld-songpanel__titlerow">
					{headerExtra}
					<div className="ld-songpanel__titlewrap">
						<h3 className="ld-songpanel__title">{title}</h3>
						{meta && <p className="ld-songpanel__meta">{meta}</p>}
					</div>
					{active !== 'lyrics' && (
						<button type="button" className="ld-songpanel__back" onClick={() => go('lyrics')}>
							<i className="fa-sharp fa-solid fa-arrow-left" aria-hidden="true" />
							Lyrics
						</button>
					)}
				</div>
				{active === 'lyrics' ? (
					<div className="ld-songpanel__mats">
						{ROWS.map(([k, label, icon]) => (
							<FactRow key={k} kind={k} label={label} icon={icon} fact={facts[k]} onOpen={() => go(k)} />
						))}
					</div>
				) : (
					<p className="ld-songpanel__modename">{modeLabel}</p>
				)}
			</header>
			<div className="ld-songpanel__body">{panes[active] || null}</div>
		</section>
	);
};

export default SongPanel;
